
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";
import { MFOItem, MFOSubTask, TaskAssignee } from "@/types/kanban";
import { fetchSubtasksForMFO, updateSubtaskStatus, recalculateAndUpdateMFOProgress } from "@/utils/supabase/subtaskUtils";
import { deleteMFO } from "@/utils/supabase/mfoUtils";

export const useMFOItems = (departmentId?: string | null) => {
  const [mfoItems, setMfoItems] = useState<MFOItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const operationInProgress = useRef(false);

  const fetchMFOItems = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Build the query
      let query = supabase
        .from("mfo_items")
        .select('*');
        
      // Filter by department if provided
      if (departmentId) {
        query = query.eq("department_id", departmentId);
      }
      
      const { data, error: mfoError } = await query;
      
      if (mfoError) throw mfoError;
      
      // For each MFO, fetch the success indicators and assignees
      const fullMFOItems = await Promise.all(
        data.map(async (mfo) => {
          // Get success indicators
          const { data: indicators, error: indError } = await supabase
            .from('mfo_success_indicators')
            .select('indicator')
            .eq('mfo_id', mfo.id);
            
          if (indError) {
            console.error("Error fetching indicators:", indError);
            return null;
          }
          
          // Get assignees through the join table
          const { data: assignees, error: assignError } = await supabase
            .from('mfo_assignees')
            .select(`
              employee_id,
              employees:employee_id (id, name, avatar)
            `)
            .eq('mfo_id', mfo.id);
            
          if (assignError) {
            console.error("Error fetching assignees:", assignError);
            return null;
          }
          
          // Get subtasks
          const subtasks = await fetchSubtasksForMFO(mfo.id);
          
          const collaborators: TaskAssignee[] = assignees.map(a => ({
            id: a.employees.id,
            name: a.employees.name,
            avatar: a.employees.avatar
          }));
          
          // Map database fields to MFOItem type with proper type conversions
          return {
            id: mfo.id,
            code: mfo.code,
            title: mfo.title,
            successIndicators: indicators.map(i => i.indicator),
            budget: Number(mfo.budget),
            timeline: {
              q1: mfo.q1,
              q2: mfo.q2,
              q3: mfo.q3,
              q4: mfo.q4
            },
            collaborators,
            departmentId: mfo.department_id,
            status: mfo.status as "not-started" | "in-progress" | "delayed" | "completed",
            priority: mfo.priority as "high" | "medium" | "low",
            progress: mfo.progress,
            dueDate: mfo.due_date,
            subTasks: subtasks
          } as MFOItem;
        })
      );
      
      // Filter out any null values from failed fetches
      setMfoItems(fullMFOItems.filter(Boolean) as MFOItem[]);
    } catch (error: any) {
      setError(error.message);
      toast({
        title: "Failed to load MFO items",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [departmentId, toast]);

  useEffect(() => {
    fetchMFOItems();
  }, [fetchMFOItems]);

  const getMFODetail = useCallback(async (id: string): Promise<MFOItem | null> => {
    try {
      // Fetch MFO item
      const { data: mfo, error: mfoError } = await supabase
        .from('mfo_items')
        .select('*')
        .eq('id', id)
        .single();
      
      if (mfoError) throw mfoError;
      
      // Get success indicators
      const { data: indicators, error: indError } = await supabase
        .from('mfo_success_indicators')
        .select('indicator')
        .eq('mfo_id', id);
        
      if (indError) throw indError;
      
      // Get assignees through the join table
      const { data: assignees, error: assignError } = await supabase
        .from('mfo_assignees')
        .select(`
          employee_id,
          employees:employee_id (id, name, avatar)
        `)
        .eq('mfo_id', id);
        
      if (assignError) throw assignError;
      
      // Get subtasks
      const subtasks = await fetchSubtasksForMFO(id);
      
      const collaborators: TaskAssignee[] = assignees.map(a => ({
        id: a.employees.id,
        name: a.employees.name,
        avatar: a.employees.avatar
      }));
      
      // Map database fields to MFOItem type with proper type conversions
      return {
        id: mfo.id,
        code: mfo.code,
        title: mfo.title,
        successIndicators: indicators.map(i => i.indicator),
        budget: Number(mfo.budget),
        timeline: {
          q1: mfo.q1,
          q2: mfo.q2,
          q3: mfo.q3,
          q4: mfo.q4
        },
        collaborators,
        departmentId: mfo.department_id,
        status: mfo.status as "not-started" | "in-progress" | "delayed" | "completed",
        priority: mfo.priority as "high" | "medium" | "low",
        progress: mfo.progress,
        dueDate: mfo.due_date,
        subTasks: subtasks
      };
    } catch (error: any) {
      toast({
        title: "Failed to load MFO details",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
  }, [toast]);

  const createMFO = async (mfoData: Partial<MFOItem>) => {
    // Use a ref to prevent duplicate submissions
    if (operationInProgress.current || isMutating) return null;
    
    try {
      operationInProgress.current = true;
      setIsMutating(true);
      
      // Implementation of MFO creation
      // This would call the existing createMFO function from mfoUtils
      // but with mutation state tracking
      
      // Refetch after creation
      await fetchMFOItems();
      return true;
    } catch (error: any) {
      toast({
        title: "Failed to create MFO",
        description: error.message,
        variant: "destructive"
      });
      return null;
    } finally {
      setIsMutating(false);
      // Use setTimeout to reset the flag after a short delay to prevent quick double-clicks
      setTimeout(() => {
        operationInProgress.current = false;
      }, 500);
    }
  };

  const updateSubtaskCompletion = async (mfoId: string, subtaskId: string, completed: boolean) => {
    if (operationInProgress.current || isMutating) return false;
    
    try {
      operationInProgress.current = true;
      setIsMutating(true);
      
      // Update subtask status
      const success = await updateSubtaskStatus(subtaskId, completed);
      
      if (!success) {
        throw new Error("Failed to update subtask");
      }
      
      // Recalculate and update MFO progress
      const newProgress = await recalculateAndUpdateMFOProgress(mfoId);
      
      if (newProgress >= 0) {
        // Update the local state
        setMfoItems(prev => 
          prev.map(mfo => {
            if (mfo.id === mfoId) {
              // Update subtask completion status
              const updatedSubTasks = mfo.subTasks?.map(st => 
                st.id === subtaskId ? { ...st, completed } : st
              );
              
              // Return updated MFO with new progress and updated subtasks
              return {
                ...mfo,
                progress: newProgress,
                subTasks: updatedSubTasks
              };
            }
            return mfo;
          })
        );
      }
      
      return true;
    } catch (error: any) {
      toast({
        title: "Failed to update subtask",
        description: error.message,
        variant: "destructive"
      });
      return false;
    } finally {
      setIsMutating(false);
      setTimeout(() => {
        operationInProgress.current = false;
      }, 500);
    }
  };

  // Add the deleteMFOItem function
  const deleteMFOItem = async (mfoId: string) => {
    if (operationInProgress.current || isMutating) return false;
    
    try {
      operationInProgress.current = true;
      setIsMutating(true);
      
      // Call the deleteMFO utility function
      await deleteMFO(mfoId);
      
      // Update the local state by removing the deleted MFO
      setMfoItems(prev => prev.filter(mfo => mfo.id !== mfoId));
      
      toast({
        title: "MFO deleted",
        description: "The MFO has been successfully deleted",
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: "Failed to delete MFO",
        description: error.message,
        variant: "destructive"
      });
      return false;
    } finally {
      setIsMutating(false);
      setTimeout(() => {
        operationInProgress.current = false;
      }, 500);
    }
  };

  return { 
    mfoItems, 
    isLoading, 
    isMutating,
    error, 
    getMFODetail, 
    updateSubtaskCompletion,
    createMFO,
    deleteMFOItem,
    refetch: fetchMFOItems
  };
};
